/**
 * v6 fix: `useHistory` -> `useNavigate`. The only call site was
 * `history.push(...)` on the sidebar coupon's "Edit" button, now
 * `navigate(...)`. Nothing else in this file touches react-router.
 */
import './Dashboard.scss';
import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { animated, useSpring, useTrail } from 'react-spring';
import ShareReferralCode from 'components/ShareReferralCode/ShareReferralCode';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import sharedMessages from 'sharedMessages';

import {
  AppWrapper,
  Block,
  Feedback,
  Spinner,ReferralsInfo
} from 'components';
import { AuthProtected, ProcessCoupon } from 'containers';
import { COUPON_LIST_DATE_FORMAT_PATTERN, COUPON_STATES,USED_COUPON_STATES } from 'coupon-common';
import { actions as couponPointsTransactionActions } from 'coupon-common/src/modules/admin/pointsManager/couponPointsTransactions';
import { actions as pointsBalancesActions } from 'coupon-common/src/modules/admin/pointsManager/pointsBalances';
import { actions as pointsTransactionsActions } from 'coupon-common/src/modules/admin/pointsManager/pointsTransactions';
import { actions as couponsActions, duck as couponsDuck } from 'coupon-common/src/modules/coupons';
import { actions } from 'coupon-common/src/modules/dashboards';
import { actions as followerActions } from 'coupon-common/src/modules/followers/duck';
import { actions as membershipInfoActions } from 'coupon-common/src/modules/memberships/info';
import { actions as pointsActivityActions } from 'coupon-common/src/modules/pointsRequests/pointsActivity';
import { actions as couponsActivityActions } from 'coupon-common/src/modules/usedCoupons/couponsActivity';
// NOTE: referral program data is NOT re-fetched here — GroupProtected already handles it
import { actions as usedCouponsListActions, duck as usedCouponsDuck } from 'coupon-common/src/modules/usedCoupons/list';
import { getNormalizedEntity,required } from 'coupon-common/src/utils';


import messages from './messages';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PERIOD_OPTION = {
  value: 'TOTAL',
  label: <FormattedMessage {...messages.totalPeriodText} />,
};

const PERIOD_OPTIONS = [
  DEFAULT_PERIOD_OPTION,
  { value: 'LAST_YEAR', label: <FormattedMessage {...messages.lastYearPeriodText} /> },
  { value: 'LAST_THIRTY_DAYS', label: <FormattedMessage {...messages.lastThirtyDaysPeriodText} /> },
  { value: 'LAST_SEVEN_DAYS', label: <FormattedMessage {...messages.lastSevenDaysPeriodText} /> },
];

const GRAPH_METRIC_OPTIONS = [
  { value: 'followers', label: 'Followers' },
  { value: 'profile_views', label: 'Profile Views' },
  { value: 'favourites', label: 'Favourites' },
];

const MONTH_OPTIONS = [
  { value: 'all', label: 'All Months' },
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

const TABLE_VIEW_OPTIONS = [
  { value: 'used_coupons', label: 'Used Coupons' },
  { value: 'points_requests', label: 'Points Requests' },
  { value: 'points_transactions', label: 'Points Transactions' },
  { value: 'referral_program', label: 'Referral Program' },
];

// Redux/DRF errors can arrive as strings, {detail}, {message}, or arrays —
// never assume it's a string before handing it to a component that renders
// it as a child. Centralized here so every <Feedback content={...}> call
// site in this file goes through the same normalization.
const getErrorMessage = (err) => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (Array.isArray(err)) return err.map(getErrorMessage).filter(Boolean).join(' ');
  if (typeof err === 'object') {
    return err.detail || err.message || err.error || 'Something went wrong.';
  }
  return String(err);
};

const usedCouponsEntityKey = usedCouponsDuck?.usedCouponsEntity?.key || 'usedCoupons';

const UsedCouponsStateToLabels = {
  [USED_COUPON_STATES.WAITING]: 'Waiting',
  [USED_COUPON_STATES.ACCEPTED]: 'Accepted',
  [USED_COUPON_STATES.REJECTED]: 'Rejected',
  [USED_COUPON_STATES.FAILED]: 'Failed',
  [USED_COUPON_STATES.CANCELED]: 'Canceled',
};

// ─── Custom Select Styles (for light hero area) ──────────────────────────────

const heroSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: '#f8fafc',
    border: state.isFocused ? '1.5px solid #e53935' : '1.5px solid #e2e8f0',
    borderRadius: 12,
    padding: '2px 6px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(229,57,53,0.1)' : 'none',
    '&:hover': { borderColor: 'rgba(229,57,53,0.4)' },
    minHeight: 42,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    padding: '10px 14px',
    background: state.isSelected ? '#e53935' : state.isFocused ? '#fff5f5' : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    cursor: 'pointer',
  }),
};

const cardSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: '#f8fafc',
    border: state.isFocused ? '1.5px solid #e53935' : '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '1px 4px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(229,57,53,0.1)' : 'none',
    '&:hover': { borderColor: '#e53935' },
    minHeight: 38,
    fontSize: '0.875rem',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    zIndex: 20,
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 12px',
    fontSize: '0.875rem',
    background: state.isSelected ? '#e53935' : state.isFocused ? '#fff5f5' : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    cursor: 'pointer',
  }),
};

// ─── Component ────────────────────────────────────────────────────────────────

export const Dashboard = ({
  intl,
  // Dashboard data
  data,
  fetching,
  fetched,
  error,
  dispatch,
  // Followers
  followerState,
  // Membership
  membershipData,
  membershipFetching,
  // Referral program
  referralData,
  referralFetching,
  referralFetched,
  referralError,
  referralInfo,
  // Used coupons
  usedCouponsListState,
  // Points requests
  pointsRequestsData,
  // Points transactions
  transactionsData,
  transactionsDataFetched,
  couponsData,
  couponsDataFetched,
  balancesData,
  balancesDataFetched,
  currentUserEmail,
  // My Coupons
  myCoupons,
  viewCounts,
}) => {
  const businessInfo = useSelector((state) => state.businessInfo.data);
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD_OPTION);

  const navigate = useNavigate();

  // Returns the earliest Date that should be shown for the selected period, or null for TOTAL
  const getPeriodCutoff = (periodValue) => {
    const now = new Date();
    switch (periodValue) {
      case 'LAST_SEVEN_DAYS':   { const d = new Date(now); d.setDate(d.getDate() - 7);   return d; }
      case 'LAST_THIRTY_DAYS':  { const d = new Date(now); d.setDate(d.getDate() - 30);  return d; }
      case 'LAST_YEAR':         { const d = new Date(now); d.setFullYear(d.getFullYear() - 1); return d; }
      default: return null; // TOTAL — no filter
    }
  };

  // Master table state
  const [activeTableView, setActiveTableView] = useState(TABLE_VIEW_OPTIONS[0]);

  // Points transactions combined data
  const [combinedTransactions, setCombinedTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  // Sidebar toggle
  const [sidebarFilter, setSidebarFilter] = useState(COUPON_STATES.PUBLISHED);

  // Main section navigation
  const [activeSection, setActiveSection] = useState('overview');

  // Used coupon modal
  const [usedCouponData, setUsedCouponData] = useState({});

  const handleCouponClick = (usedCoupon) => {
    setUsedCouponData(usedCoupon);
    dispatch(couponsActivityActions.openModal());
  };


  // ─── Data Fetching ────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(actions.getData(DEFAULT_PERIOD_OPTION.value));
    dispatch(followerActions.getBusinessFollowers());
    dispatch(membershipInfoActions.getMembershipInfo());
    // NOTE: Do NOT dispatch referralProgramActions.getBusinessReferralProgram() here.
    // GroupProtected wraps this component and depends on businessReferralProgram.fetching
    // being stable. Re-dispatching sets fetching=true, which causes GroupProtected to
    // unmount Dashboard, creating an infinite mount/unmount cycle.
    // The referral data is already available by the time Dashboard renders.
    dispatch(usedCouponsListActions.getUsedCoupons());
    dispatch(couponsActions.getBusinessCoupons());
    // Points transaction fetches — wrapped in try/catch as admin modules may not be available
    try {
      dispatch(pointsTransactionsActions.getPointsTransactions());
      dispatch(couponPointsTransactionActions.getCouponTransactions({}));
      dispatch(pointsBalancesActions.getPointsBalances());
    } catch (e) {
      console.warn('Dashboard: Points transaction dispatches failed (admin modules may not be available)', e);
    }
  }, [dispatch]);

  // Fetch points requests if business ID is available
  useEffect(() => {
    if (businessInfo?.id) {
      dispatch(pointsActivityActions.getPointsRequests({ business: businessInfo.id }));
    }
  }, [dispatch, businessInfo?.id]);

  // ─── Points Transactions Processing ───────────────────────────────────

  const processedTransactions = useMemo(() => {
    if (!transactionsDataFetched || !couponsDataFetched || !balancesDataFetched) return null;

    const txData = (transactionsData || []).map((tx) => ({
      ...tx,
      id: `P${String(tx.id).padStart(4, '0')}`,
    }));

    // couponsData can be an array or an object with .results depending on state
    const couponResults = Array.isArray(couponsData)
      ? couponsData
      : (couponsData?.results || []);

    const couponTxData = couponResults.map((tx) => ({
      id: `C${String(tx.id).padStart(4, '0')}`,
      time: tx.transaction_datetime,
      amount: tx.points_delta,
      from_user: tx.from_email && tx.from_email.trim() !== '' ? tx.from_email : 'ADMIN',
      to_user: tx.to_email,
      status: 'success',
      notes: tx.transaction_type,
      previous_balance: null,
      new_balance: null,
      from_user_name: tx.from_email && tx.from_email.trim() !== '' ? tx.from_user_name : 'ADMIN',
      to_user_name: tx.to_user_name,
    }));

    const merged = [...txData, ...couponTxData]
      .filter((d) => d.from_user === currentUserEmail || d.to_user === currentUserEmail)
      .sort((a, b) => new Date(b.time) - new Date(a.time));

    // Fill missing balances
    let currentBalance =
      Number(
        (balancesData || []).find((item) => item.email === currentUserEmail)?.points_at_last_date
      ) || 0;

    for (let i = 0; i < merged.length; i++) {
      const tx = merged[i];
      const amount = Number(tx.amount) || 0;
      if (tx.to_user === currentUserEmail) {
        tx.new_balance = currentBalance;
        tx.previous_balance = Number((currentBalance - amount).toFixed(2));
        currentBalance = tx.previous_balance;
      } else if (tx.from_user === currentUserEmail) {
        tx.new_balance = currentBalance;
        tx.previous_balance = Number((currentBalance + amount).toFixed(2));
        currentBalance = tx.previous_balance;
        tx.amount = -Math.abs(amount);
      }
    }

    return merged;
  }, [transactionsData, transactionsDataFetched, couponsData, couponsDataFetched, balancesData, balancesDataFetched, currentUserEmail]);

  useEffect(() => {
    if (processedTransactions) {
      setCombinedTransactions(processedTransactions);
      setTransactionsLoading(false);
    }
  }, [processedTransactions]);

  // ─── Used Coupons Processing ──────────────────────────────────────────

  const usedCouponsNormalized = useMemo(() => {
    try {
      if (usedCouponsListState && usedCouponsEntityKey) {
        return getNormalizedEntity(
          usedCouponsListState,
          usedCouponsEntityKey,
          intl,
          messages.usedCouponsEntity || { id: 'app.usedCouponsEntity', defaultMessage: 'used coupons' },
        );
      }
    } catch (e) {
      console.warn('Dashboard: getNormalizedEntity failed for usedCoupons', e);
    }
    return { entityList: [], isFetching: false, error: null, isNextPageExist: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedCouponsListState, usedCouponsEntityKey]);

  const { entityList: usedCoupons, isFetching: isUsedCouponsFetching, error: usedCouponsError } = usedCouponsNormalized;

  // ─── Points Requests Processing ───────────────────────────────────────

  const pointsRequestsList = useMemo(() => {
    // pointsRequestsData can be an array or an object with .results
    const results = Array.isArray(pointsRequestsData)
      ? pointsRequestsData
      : pointsRequestsData?.results;
    if (!results) return [];
    return results.map((req) => ({
      id: req.id,
      user: req.user,
      business: req.business,
      points_rate: req.points_rate,
      first_name: req.customer?.first_name,
      date_and_time: new Date(req.date_time).toLocaleString(),
      points: req.amount,
      status: req.status,
    }));
  }, [pointsRequestsData]);

  // ─── My Coupons Processing ────────────────────────────────────────────

  const couponsEntityKey = couponsDuck?.couponsEntity?.key || 'coupons';
  
  const {
    entityList: couponsList,
    isFetching: isCouponsFetching,
  } = useMemo(() => {
    try {
      if (myCoupons && couponsEntityKey) {
        return getNormalizedEntity(
          myCoupons,
          couponsEntityKey,
          intl,
          { id: 'app.couponsEntity', defaultMessage: 'coupons' },
        );
      }
    } catch (e) {
      console.warn('Dashboard: getNormalizedEntity failed for coupons', e);
    }
    return { entityList: [], isFetching: false, error: null, isNextPageExist: false };
  }, [myCoupons, couponsEntityKey, intl]);

  // Published count for the sidebar counter (always based on full list)
  const publishedCoupons = useMemo(() => {
    return couponsList.filter(coupon => coupon.state === COUPON_STATES.PUBLISHED);
  }, [couponsList]);

  // Count for the active tab (unsliced) for the counter display
  const activeTabCount = useMemo(() => {
    if (sidebarFilter === COUPON_STATES.PUBLISHED) return publishedCoupons.length;
    if (sidebarFilter === COUPON_STATES.DRAFT)
      return couponsList.filter(c => c.state === COUPON_STATES.DRAFT).length;
    return couponsList.filter(
      c => c.state !== COUPON_STATES.PUBLISHED && c.state !== COUPON_STATES.DRAFT
    ).length;
  }, [couponsList, publishedCoupons, sidebarFilter]);

  // Sidebar list — switches based on toggle
  const filteredSidebarCoupons = useMemo(() => {
    if (sidebarFilter === COUPON_STATES.PUBLISHED) {
      return publishedCoupons;
    }
    if (sidebarFilter === COUPON_STATES.DRAFT) {
      return couponsList.filter(c => c.state === COUPON_STATES.DRAFT);
    }
    return couponsList.filter(
      c => c.state !== COUPON_STATES.PUBLISHED && c.state !== COUPON_STATES.DRAFT
    );
  }, [couponsList, publishedCoupons, sidebarFilter]);

  // ─── Activity Alert Counts ────────────────────────────────────────────

  const waitingCouponsCount = useMemo(
    () => usedCoupons.filter((c) => c.state === USED_COUPON_STATES.WAITING).length,
    [usedCoupons],
  );

  const pendingPointsCount = useMemo(
    () => pointsRequestsList.filter((r) => r.status === 'pending').length,
    [pointsRequestsList],
  );

  // ─── Period-filtered Activity Data ───────────────────────────────────

  const periodCutoff = useMemo(
    () => getPeriodCutoff(selectedPeriod.value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPeriod.value],
  );

  const filteredUsedCoupons = useMemo(() => {
    if (!periodCutoff) return usedCoupons;
    return usedCoupons.filter(c => new Date(c.created_at) >= periodCutoff);
  }, [usedCoupons, periodCutoff]);

  const filteredTransactions = useMemo(() => {
    if (!periodCutoff) return combinedTransactions;
    return combinedTransactions.filter(tx => new Date(tx.time) >= periodCutoff);
  }, [combinedTransactions, periodCutoff]);

  const filteredPointsRequests = useMemo(() => {
    if (!periodCutoff) return pointsRequestsList;
    return pointsRequestsList.filter(req => new Date(req.date_and_time) >= periodCutoff);
  }, [pointsRequestsList, periodCutoff]);

  // ─── Animations ───────────────────────────────────────────────────────

  const heroSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(-20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 200, friction: 20 },
  });

  const cardsTrail = useTrail(3, {
    from: { opacity: 0, transform: 'scale(0.95) translateY(16px)' },
    to: {
      opacity: fetched ? 1 : 0,
      transform: fetched ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(16px)',
    },
    config: { tension: 200, friction: 22 },
  });

  // ─── Greeting ─────────────────────────────────────────────────────────

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // ─── Period Form ──────────────────────────────────────────────────────

  const onSelectChange = (input, value, form) => {
    input.onChange(value);
    setSelectedPeriod(value);
    form.submit();
  };

  const onSubmit = (values) => {
    dispatch(actions.getData(values.range.value));
  };

  // ─── Copy Referral Link ───────────────────────────────────────────────

  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyReferralLink = () => {
    const code = referralData?.referral_code;
    if (code) {
      const link = `https://mycouponbook.ca/signup?ref=${code}`;
      navigator.clipboard.writeText(link).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2500);
      });
    }
  };

  // ─── Render: Period Selector ──────────────────────────────────────────

  const renderPeriodSelector = () => (
    <Form
      onSubmit={onSubmit}
      render={({ form, handleSubmit }) => (
        <form onSubmit={handleSubmit} className="period-selector-form">
          <Field
            name="range"
            validate={(option) => required(option.value)}
            defaultValue={DEFAULT_PERIOD_OPTION}
          >
            {({ input, meta }) => (
              <div className="period-selector-wrapper">
                <Select
                  {...input}
                  options={PERIOD_OPTIONS}
                  value={input.value}
                  getOptionLabel={(option) => option.label}
                  onChange={(value) => onSelectChange(input, value, form)}
                  isDisabled={fetching}
                  styles={heroSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <Feedback
                  invalid
                  fullWidth={false}
                  show={Boolean(!fetching && error)}
                  content={getErrorMessage(error)}
                />
              </div>
            )}
          </Field>
        </form>
      )}
    />
  );

  // ─── Render: Stat Cards Row ───────────────────────────────────────────

  const renderStatCards = () => {
    const stats = [
      {
        title: intl.formatMessage(messages.totalFollowersBlockHeading),
        value: followerState?.data?.followers_count || 0,
        label: intl.formatMessage(messages.followersText, {
          followers: followerState?.data?.followers_count || 0,
        }),
        icon: '👥',
        accent: '#e53935',
      },
      {
        title: 'Total Coupons Used',
        value:
          (data.amount_off_coupons_count || 0) + (data.points_back_coupons_count || 0),
        label: 'Coupons Redeemed',
        icon: '🎟️',
        accent: '#ff7043',
      },
      {
        title: 'Membership Plan',
        value: membershipFetching ? '---' : membershipData?.name || 'Free',
        label: membershipFetching
          ? 'Loading...'
          : `${membershipData?.available_coupons ?? '—'} coupons available`,
        icon: '👑',
        accent: '#fdd835',
        isMembership: true,
      },
    ];

    return (
      <div className="stats-row">
        {cardsTrail.map((style, index) => (
          <animated.div key={index} style={style} className="stat-card-wrapper">
            <div className="stat-card" style={{ '--card-accent': stats[index].accent }}>
              <div className="stat-card-icon-area">
                <span className="stat-card-icon">{stats[index].icon}</span>
              </div>
              <div className="stat-card-body">
                <p className="stat-card-title">{stats[index].title}</p>
                <h3 className="stat-card-value">{stats[index].value}</h3>
                <p className="stat-card-label">{stats[index].label}</p>
              </div>
              {stats[index].isMembership && (
                <div className="membership-meta">
                  <div className="membership-meta-row">
                    <span className="meta-dot meta-dot--slots" />
                    <span>Premium Slots: {membershipData?.available_premium_memberships ?? '—'}</span>
                  </div>
                  <div className="membership-meta-row">
                    <span className="meta-dot meta-dot--sponsor" />
                    <span>Sponsorships: {membershipData?.available_sponsorships_slots ?? '—'}</span>
                  </div>
                </div>
              )}
            </div>
          </animated.div>
        ))}
      </div>
    );
  };

  // ─── Render: Overview Bar Chart ──────────────────────────────────────

  const renderOverviewBarChart = () => {
    const overviewData = [
      { name: 'Followers', value: followerState?.data?.followers_count || 0, color: '#e53935' },
      { name: 'Profile Views', value: data.profile_views_count || 0, color: '#ff7043' },
      { name: 'Favourites', value: data.favourites_count || 0, color: '#fdd835' },
      { name: 'Redemptions', value: (data.amount_off_coupons_count || 0) + (data.points_back_coupons_count || 0), color: '#43a047' },
    ];

    return (
      <div className="graph-card">
        <div className="graph-card-header">
          <h5 className="graph-card-title">Overview</h5>
          <div className="graph-card-legend">
            {overviewData.map(item => (
              <span key={item.name} className="legend-item">
                <span className="legend-dot" style={{ background: item.color }} />
                {item.name}
              </span>
            ))}
          </div>
        </div>
        <div className="graph-card-body">
          {fetched ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={overviewData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    fontSize: '0.8rem',
                  }}
                  formatter={(value, name) => [value, name]}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  {overviewData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="graph-placeholder">
              <Spinner show={fetching} />
              {!fetching && <p className="graph-placeholder-text">No data available</p>}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Render: Referral Program Card ────────────────────────────────────

  // ─── Render: Points & Dollars Received Line Chart ─────────────────────

  // Merge points and dollars chart data into a single dataset for the line chart
  const pointsChartRaw = data.amount_points_received_chart || [];
  const dollarsChartRaw = data.amount_dollars_received_chart || [];
  
  const combinedLineChartData = useMemo(() => {
    if (!pointsChartRaw.length && !dollarsChartRaw.length) return [];
    
    // Determine which array is longer to use as base
    const maxLength = Math.max(pointsChartRaw.length, dollarsChartRaw.length);
    
    const result = Array.from({ length: maxLength }, (_, index) => {
      const pointsItem = pointsChartRaw[index];
      const dollarsItem = dollarsChartRaw[index];
      
      // Get the name/label from whichever exists
      const name = pointsItem?.name || pointsItem?.label || pointsItem?.month || 
                   dollarsItem?.name || dollarsItem?.label || dollarsItem?.month || 
                   `${index + 1}`;
      
      // Use the actual property names from your data structure
      const pointsValue = pointsItem?.points ?? pointsItem?.points_back ?? 
                         pointsItem?.value ?? pointsItem?.amount ?? 0;
      const dollarsValue = dollarsItem?.dollars ?? dollarsItem?.amount_off ?? 
                          dollarsItem?.value ?? dollarsItem?.amount ?? 0;
      
      return {
        name,
        points: pointsValue,
        dollars: dollarsValue,
      };
    });
    
    return result;
  }, [pointsChartRaw, dollarsChartRaw]);

  const renderPointsDollarsChart = () => {
    const hasData = combinedLineChartData.length > 0;

    return (
      <div className="graph-card">
        <div className="graph-card-header">
          <h5 className="graph-card-title">Redemptions</h5>
          <div className="graph-card-legend">
            <span className="legend-item">
              <span className="legend-dot" style={{ background: '#e53935' }} />
              Points
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ background: '#ff7043' }} />
              Dollars
            </span>
          </div>
          <div className="graph-card-totals">
            <span className="graph-total">
              <strong>Points</strong> {data.points_from_customers || 0}
            </span>
            <span className="graph-total">
              <strong>Dollars</strong> {data.dollars_from_customers || 0}
            </span>
          </div>
        </div>
        <div className="graph-card-body">
          {fetched && hasData ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={combinedLineChartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    fontSize: '0.8rem',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="points"
                  name="Points"
                  stroke="#e53935"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#e53935', strokeWidth: 2, stroke: 'white' }}
                  activeDot={{ r: 6, fill: '#e53935', strokeWidth: 2, stroke: 'white' }}
                />
                <Line
                  type="monotone"
                  dataKey="dollars"
                  name="Dollars"
                  stroke="#ff7043"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#ff7043', strokeWidth: 2, stroke: 'white' }}
                  activeDot={{ r: 6, fill: '#ff7043', strokeWidth: 2, stroke: 'white' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="graph-placeholder">
              <Spinner show={fetching} />
              {!fetching && <p className="graph-placeholder-text">No redemption data available</p>}
            </div>
          )}
        </div>
      </div>
    );
  };

  const pointsEarnPerYear = referralInfo?.data?.first_level_referral_points || 500;
  const remainingPremiums = membershipData?.available_premium_memberships ?? 0;

  const renderReferralCard = () => (
    <div className="referral-card">
      <div className="referral-card-header">
        <div className="referral-card-icon-wrap">
          <span className="referral-card-icon">🎁</span>
        </div>
        <h4 className="referral-card-title">Referral Program</h4>
      </div>

      <div className="referral-stats-row">
        <div className="referral-stat">
          <span className="referral-stat-value">{referralData?.referrals?.length || 0}</span>
          <span className="referral-stat-label">Total Referrals</span>
        </div>
        <div className="referral-stat">
          <span className="referral-stat-value">{referralData?.points_earned || 0}</span>
          <span className="referral-stat-label">Points Earned</span>
        </div>
        <div className="referral-stat">
          <span className="referral-stat-value">{remainingPremiums}</span>
          <span className="referral-stat-label">Premium Gifts Left</span>
        </div>
      </div>

      <div className="referral-copy-blocks">
        <p className="referral-copy-text">
          Share the app using your referral link and earn <strong>{pointsEarnPerYear} points</strong> every year your referral joins our Premium Membership.
        </p>
        <p className="referral-copy-text">
          Give the gift of Premium to your customers. Your customers can get one full year of Premium when they download the app using your referral link.
        </p>
        <p className="referral-copy-text referral-copy-highlight">
          You can give <strong>{remainingPremiums}</strong> Premium Membership{remainingPremiums !== 1 ? 's' : ''}.
        </p>
      </div>

      <div className="referral-actions">
       <ShareReferralCode code={referralData?.referral_code} />
      
      </div>

      {referralFetched && referralData?.marketer && (
        <div className="referral-marketer">
          <p className="referral-marketer-label">Your Marketer</p>
          <p className="referral-marketer-name">
            {referralData.marketer.first_name} {referralData.marketer.last_name}
          </p>
        </div>
      )}

      <Feedback invalid fullWidth={false} show={Boolean(referralError)} content={getErrorMessage(referralError)} />
    </div>
  );

  // ─── Render: Activity Alert Badges ───────────────────────────────────

  const renderActivityAlerts = () => {
    if (!waitingCouponsCount && !pendingPointsCount) return null;

    const goToActivity = (tableOption) => {
      setActiveSection('activity');
      setActiveTableView(tableOption);
    };

    return (
      <div className="activity-alerts">
        {waitingCouponsCount > 0 && (
          <button
            className="activity-alert activity-alert--coupons"
            onClick={() => goToActivity(TABLE_VIEW_OPTIONS[0])}
            title="View waiting coupon requests"
          >
            <span className="activity-alert-icon">🎟️</span>
            <span className="activity-alert-text">Coupons</span>
            <span className="activity-alert-badge">{waitingCouponsCount}</span>
          </button>
        )}
        {pendingPointsCount > 0 && (
          <button
            className="activity-alert activity-alert--points"
            onClick={() => goToActivity(TABLE_VIEW_OPTIONS[1])}
            title="View pending points requests"
          >
            <span className="activity-alert-icon">⭐</span>
            <span className="activity-alert-text">Points</span>
            <span className="activity-alert-badge">{pendingPointsCount}</span>
          </button>
        )}
      </div>
    );
  };
  

  // ─── Render: Master Data Table ────────────────────────────────────────

  const renderMasterTable = () => {
    return (
      <div className="master-table-card">
        <div className="master-table-header">
          <h4 className="master-table-title">Activity Center</h4>
          <Select
            options={TABLE_VIEW_OPTIONS}
            value={activeTableView}
            onChange={setActiveTableView}
            styles={cardSelectStyles}
            className="table-view-select"
            isSearchable={false}
          />
        </div>
        <div className="master-table-body">
          {activeTableView.value === 'used_coupons' && renderUsedCouponsTable()}
          {activeTableView.value === 'points_requests' && renderPointsRequestsTable()}
          {activeTableView.value === 'points_transactions' && renderPointsTransactionsTable()}
          {activeTableView.value === 'referral_program' && renderReferralTable()}
        </div>
      </div>
    );
  };

  // ── Used Coupons Table ──

  const renderUsedCouponsTable = () => {
    if (isUsedCouponsFetching) return <div className="table-loading"><Spinner show /></div>;
    if (usedCouponsError) return <Feedback show invalid content={getErrorMessage(usedCouponsError)} />;
    if (!filteredUsedCoupons || filteredUsedCoupons.length === 0) return <p className="table-empty">No used coupons for this period.</p>;

    return (
      <div className="table-scroll">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Coupon Title</th>
              <th>Customer</th>
              <th>Date & Time</th>
              <th>Pay By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsedCoupons.slice(0, 25).map((coupon) => (
              <tr key={coupon.id}>
                <td
                  className="td-title td-clickable"
                  onClick={() => handleCouponClick(coupon)}
                >
                  {coupon.coupon_name}
                </td>
                <td>{coupon.customer_name}</td>
                <td>{intl.formatDate(coupon.created_at, COUPON_LIST_DATE_FORMAT_PATTERN)}</td>
                <td>{coupon.pay_by_points ? 'Points' : 'Dollars'}</td>
                <td>
                  <span className={`status-badge status-badge--${(coupon.state || '').toLowerCase()}`}>
                    {UsedCouponsStateToLabels[coupon.state] || coupon.state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Points Requests Table ──

  const renderPointsRequestsTable = () => {
    if (!filteredPointsRequests || filteredPointsRequests.length === 0) {
      return <p className="table-empty">No points requests for this period.</p>;
    }

    return (
      <div className="table-scroll">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Date & Time</th>
              <th>Points</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredPointsRequests.slice(0, 25).map((req) => (
              <tr key={req.id}>
                <td>{req.first_name}</td>
                <td>{req.date_and_time}</td>
                <td>{req.points}</td>
                <td>
                  <span className={`status-badge status-badge--${(req.status || '').toLowerCase()}`}>
                    {req.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Points Transactions Table ──

  const renderPointsTransactionsTable = () => {
    if (transactionsLoading) return <div className="table-loading"><Spinner show /></div>;
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return <p className="table-empty">No transactions for this period.</p>;
    }

    return (
      <div className="table-scroll">
        <table className="dash-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Time</th>
              <th>From</th>
              <th>To</th>
              <th>Prev Balance</th>
              <th>Amount</th>
              <th>New Balance</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.slice(0, 25).map((tx) => (
              <tr key={tx.id}>
                <td className="td-mono">{tx.id}</td>
                <td>{new Date(tx.time).toLocaleString()}</td>
                <td>
                  {tx.from_user === currentUserEmail || tx.from_user === 'charles.anthony.cruz02@gmail.com'
                    ? tx.from_user
                    : tx.from_user_name}
                </td>
                <td>{tx.to_user === currentUserEmail ? tx.to_user : tx.to_user_name}</td>
                <td>{tx.previous_balance}</td>
                <td className={tx.to_user === currentUserEmail && tx.amount > 0 ? 'td-positive' : 'td-negative'}>
                  {tx.amount}
                </td>
                <td>{tx.new_balance}</td>
                <td>
                  <span className={`status-badge status-badge--${(tx.status || '').toLowerCase()}`}>
                    {tx.status}
                  </span>
                </td>
                <td>{tx.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Referral Table ──

const renderReferralTable = () => {
  const referrals = referralData?.referrals;
  if (referralFetching) return <div className="table-loading"><Spinner show /></div>;
  if (!referrals || referrals.length === 0) return <p className="table-empty">No referrals yet. Share your code to start earning!</p>;

  return <ReferralsInfo data={referrals} />;
};

  // ─── Render: Coupons Sidebar ──────────────────────────────────────────

  const renderCouponsSidebar = () => {
    return (
      <div className="coupons-sidebar">
        <div className="coupons-sidebar-header">
          <h4 className="coupons-sidebar-title">🎟️ Coupons</h4>
          <button 
            className="coupons-add-btn"
            onClick={() => window.location.href = '/coupons/create'}
          >
            +
          </button>
        </div>
        
        <div className="coupons-count">
          <span className="coupons-count-number">
            {String(activeTabCount).padStart(2, '0')}
          </span>
          <span className="coupons-count-separator">/</span>
          <span className="coupons-count-total">
            {String(couponsList.length).padStart(2, '0')}
          </span>
        </div>

        <div className="coupons-toggle-buttons">
          <button
            className={`coupons-toggle-btn ${sidebarFilter === COUPON_STATES.PUBLISHED ? 'active' : ''}`}
            onClick={() => setSidebarFilter(COUPON_STATES.PUBLISHED)}
          >
            <span className="toggle-icon">🌐</span> Published
          </button>
          <button
            className={`coupons-toggle-btn ${sidebarFilter === COUPON_STATES.DRAFT ? 'active' : ''}`}
            onClick={() => setSidebarFilter(COUPON_STATES.DRAFT)}
          >
            <span className="toggle-icon">📝</span> Draft
          </button>
          <button
            className={`coupons-toggle-btn ${sidebarFilter === 'unpublished' ? 'active' : ''}`}
            onClick={() => setSidebarFilter('unpublished')}
          >
            <span className="toggle-icon">⏰</span> Expired
          </button>
        </div>

        <div className="coupons-list">
          {isCouponsFetching ? (
            <div className="coupons-loading">
              <Spinner show />
            </div>
          ) : filteredSidebarCoupons.length === 0 ? (
            <p className="coupons-empty">
              No {sidebarFilter === COUPON_STATES.PUBLISHED ? 'published' : sidebarFilter === COUPON_STATES.DRAFT ? 'draft' : 'expired'} coupons yet
            </p>
          ) : (
            filteredSidebarCoupons.map((coupon) => (
              <div key={coupon.id} className="coupon-item">
                <div className="coupon-item-check">
                  <div className="coupon-check-circle">✓</div>
                </div>
                <div className="coupon-item-content">
                  <h5 className="coupon-item-title">{coupon.title}</h5>
                  <p className="coupon-item-expiry">
                    Expire: {coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : 'No expiry'}
                  </p>
                  <button
                    type="button"
                    className="coupon-item-edit-link"
                    onClick={() => navigate(`/coupons/${coupon.id}?mode=edit&step=5`)}
                  >
                    ✏️ Edit
                  </button>
                </div>
                <div className="coupon-item-stats">
                  <div className="coupon-stat">
                    <span className="coupon-stat-icon">❤️</span>
                    <span className="coupon-stat-value">{coupon.save_coupon_count || 0}</span>
                  </div>
                  <div className="coupon-stat">
                    <span className="coupon-stat-icon">👁️</span>
                    <span className="coupon-stat-value">{viewCounts?.[coupon.id] || 0}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────

  return (
    <AppWrapper showHeader showSideNav showHeadingRow={false}>
      <AuthProtected />
      <div className="modern-dashboard">
        <div className="dashboard-main-content">
          {/* ── Hero ── */}
          <animated.div style={heroSpring} className="dashboard-hero">
            <div className="hero-content">
              <h1 className="hero-greeting">
                {getGreeting()}, {businessInfo?.name || 'there'}!
              </h1>
              <p className="hero-subtitle">
                Here's your business overview
                {selectedPeriod.value !== 'TOTAL' && selectedPeriod.label
                  ? ` for the ${typeof selectedPeriod.label === 'string'
                      ? selectedPeriod.label.toLowerCase()
                      : 'selected period'}`
                  : ''}
              </p>
            </div>
            <div className="hero-controls">
              {renderPeriodSelector()}
              <Spinner show={fetching} />
              <Feedback invalid fullWidth={false} show={Boolean(!fetching && error)} content={getErrorMessage(error)} />
            </div>
          </animated.div>

          {/* ── Section Pills ── */}
          <div className="section-pills">
            <button
              className={`section-pill ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              📊 Overview
            </button>
            <button
              className={`section-pill ${activeSection === 'referral' ? 'active' : ''}`}
              onClick={() => setActiveSection('referral')}
            >
              🎁 Referral Program
            </button>
            <button
              className={`section-pill ${activeSection === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveSection('activity')}
            >
              📋 Activity Center
            </button>
            {renderActivityAlerts()}
          </div>

          {/* ── Sections ── */}
          {activeSection === 'overview' && (
            <>
              {renderStatCards()}
              <div className="charts-row">
                {renderOverviewBarChart()}
                {renderPointsDollarsChart()}
              </div>
            </>
          )}

        {activeSection === 'referral' && (
          <div className="referral-row">
            {renderReferralCard()}
            <div className="master-table-card">
              <div className="master-table-header">
                <h4 className="master-table-title">Your Referrals</h4>
              </div>
              <div className="master-table-body">
                {renderReferralTable()}
              </div>
            </div>
          </div>
        )}

          {activeSection === 'activity' && renderMasterTable()}
        </div>

        {/* ── Coupons Sidebar ── */}
        {renderCouponsSidebar()}
      </div>
      <ProcessCoupon usedCouponData={usedCouponData} />
    </AppWrapper>
  );
};

Dashboard.propTypes = {
  data: PropTypes.object,
  fetching: PropTypes.bool,
  fetched: PropTypes.bool,
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  followerState: PropTypes.object,
  membershipData: PropTypes.object,
  membershipFetching: PropTypes.bool,
  referralData: PropTypes.object,
  referralFetching: PropTypes.bool,
  referralFetched: PropTypes.bool,
  referralError: PropTypes.string,
  referralInfo: PropTypes.object,
  usedCouponsListState: PropTypes.object,
  pointsRequestsData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  transactionsData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  transactionsDataFetched: PropTypes.bool,
  couponsData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  couponsDataFetched: PropTypes.bool,
  balancesData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  balancesDataFetched: PropTypes.bool,
  currentUserEmail: PropTypes.string,
  myCoupons: PropTypes.object,
  viewCounts: PropTypes.object,
};

// Stable default references — MUST be defined outside mapStateToProps
// to prevent new object/array identity on every call (causes infinite re-renders)
const EMPTY_OBJ = {};
const EMPTY_ARR = [];
const DEFAULT_FOLLOWERS = { data: {} };

const mapStateToProps = (state) => ({
  // Dashboard
  ...state.dashboards,
  // Followers
  followerState: state.followers || DEFAULT_FOLLOWERS,
  // Membership Info
  membershipData: (state.memberships && state.memberships.info && state.memberships.info.data && state.memberships.info.data.current_plan) || EMPTY_OBJ,
  membershipFetching: !!(state.memberships && state.memberships.info && state.memberships.info.fetching),
  // Referral Program
  referralData: (state.businessReferralProgram && state.businessReferralProgram.data) || EMPTY_OBJ,
  referralFetching: !!(state.businessReferralProgram && state.businessReferralProgram.fetching),
  referralFetched: !!(state.businessReferralProgram && state.businessReferralProgram.fetched),
  referralError: (state.businessReferralProgram && state.businessReferralProgram.error) || null,
  referralInfo: (state.memberships && state.memberships.referralInfo) || EMPTY_OBJ,
  // Used Coupons
  usedCouponsListState: (state.usedCoupons && state.usedCoupons.list) || EMPTY_OBJ,
  // Points Requests
  pointsRequestsData: (state.pointsRequests && state.pointsRequests.pointsActivity && state.pointsRequests.pointsActivity.data) || EMPTY_OBJ,
  // Points Transactions (admin state — may not exist for all users)
  transactionsData: (state.admin && state.admin.pointsManager && state.admin.pointsManager.pointsTransactions && state.admin.pointsManager.pointsTransactions.data) || EMPTY_ARR,
  transactionsDataFetched: !!(state.admin && state.admin.pointsManager && state.admin.pointsManager.pointsTransactions && state.admin.pointsManager.pointsTransactions.fetched),
  couponsData: (state.admin && state.admin.pointsManager && state.admin.pointsManager.couponPointsTransactions && state.admin.pointsManager.couponPointsTransactions.data) || EMPTY_ARR,
  couponsDataFetched: !!(state.admin && state.admin.pointsManager && state.admin.pointsManager.couponPointsTransactions && state.admin.pointsManager.couponPointsTransactions.fetched),
  balancesData: (state.admin && state.admin.pointsManager && state.admin.pointsManager.pointsBalances && state.admin.pointsManager.pointsBalances.data) || EMPTY_ARR,
  balancesDataFetched: !!(state.admin && state.admin.pointsManager && state.admin.pointsManager.pointsBalances && state.admin.pointsManager.pointsBalances.fetched),
  currentUserEmail: (state.businessInfo && state.businessInfo.data && state.businessInfo.data.email) || '',
  // My Coupons
  myCoupons: state.coupons || EMPTY_OBJ,
  viewCounts: (state.coupons && state.coupons.viewCounts) || EMPTY_OBJ,
});

export default connect(mapStateToProps)(injectIntl(Dashboard));